import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateOnboardingStep, completeOnboarding } from '../store/slices/authSlice';
import { toast } from 'react-toastify';
import './Onboarding.css';

const Onboarding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { user, loading, error } = useSelector(state => state.auth);
  
  // Get step from URL parameter, default to 1
  const stepFromUrl = parseInt(searchParams.get('step'), 10) || 1;
  const [currentStep, setCurrentStep] = useState(stepFromUrl);
  const [formData, setFormData] = useState({
    businessType: '',
    industry: '',
    businessDescription: '',
    timezone: 'UTC+5:30',
    businessHours: {
      start: '09:00',
      end: '17:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    },
    wabaId: '',
    phoneNumberId: '',
    metaAccessToken: ''
  });

  // Redirect if onboarding is already completed
  useEffect(() => {
    if (user && user.onboardingCompleted) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const totalSteps = 4;

  const steps = [
    { number: 1, title: 'Business Info', icon: '🏢' },
    { number: 2, title: 'Business Hours', icon: '🕐' },
    { number: 3, title: 'WhatsApp Setup', icon: '💬' },
    { number: 4, title: 'Review', icon: '✓' }
  ];

  const businessTypes = [
    'Retail',
    'E-commerce',
    'Service Provider',
    'Restaurant',
    'Healthcare',
    'Education',
    'Real Estate',
    'Other'
  ];

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Retail',
    'Food & Beverage',
    'Education',
    'Real Estate',
    'Other'
  ];

  const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBusinessHoursChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [field]: value
      }
    }));
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        days: prev.businessHours.days.includes(day)
          ? prev.businessHours.days.filter(d => d !== day)
          : [...prev.businessHours.days, day]
      }
    }));
  };

  const handleNext = () => {
    // Validate required fields before moving to next step
    if (currentStep === 1) {
      if (!formData.businessType || !formData.industry) {
        toast.error('Please fill in all required fields');
        return;
      }
    } else if (currentStep === 3) {
      if (!formData.wabaId) {
        toast.error('WhatsApp Business Account ID is required');
        return;
      }
    }

    // Save current step data to backend before moving forward
    if (currentStep === 1 || currentStep === 2 || currentStep === 3) {
      console.log(`[Onboarding] Saving Step ${currentStep} data:`, formData);
      
      const stepData = {
        businessType: formData.businessType,
        industry: formData.industry,
        businessDescription: formData.businessDescription,
        timezone: formData.timezone,
        businessHours: formData.businessHours,
        wabaId: formData.wabaId,
        phoneNumberId: formData.phoneNumberId,
        metaAccessToken: formData.metaAccessToken
      };
      
      dispatch(updateOnboardingStep({ step: currentStep, data: stepData }))
        .unwrap()
        .catch(err => {
          console.error(`[Onboarding] Failed to save Step ${currentStep}:`, err);
          toast.error(err.message || `Failed to save Step ${currentStep}`);
        });
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // Validate Step 3 WABA ID before completion
    if (!formData.wabaId) {
      toast.error('Please enter your WhatsApp Business Account ID');
      return;
    }

    console.log('[Onboarding] handleComplete - Submitting all form data:', formData);
    
    // Prepare complete data including all fields
    const completeData = {
      businessType: formData.businessType,
      industry: formData.industry,
      businessDescription: formData.businessDescription,
      timezone: formData.timezone,
      businessHours: formData.businessHours,
      wabaId: formData.wabaId,
      phoneNumberId: formData.phoneNumberId,
      metaAccessToken: formData.metaAccessToken
    };
    
    console.log('[Onboarding] Calling completeOnboarding with:', completeData);
    
    dispatch(completeOnboarding(completeData))
      .unwrap()
      .then((result) => {
        console.log('[Onboarding] Onboarding completed successfully:', result);
        toast.success('Onboarding completed! Welcome to Hawktrion');
        // Refresh WABA status after onboarding completion
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      })
      .catch(err => {
        console.error('[Onboarding] Failed to complete onboarding:', err);
        toast.error(err.message || 'Failed to complete onboarding');
      });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h2>Tell us about your business</h2>
            <div className="form-group">
              <label>Business Type</label>
              <select
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select business type</option>
                {businessTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Industry</label>
              <select
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select industry</option>
                {industries.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Business Description</label>
              <textarea
                name="businessDescription"
                value={formData.businessDescription}
                onChange={handleChange}
                placeholder="Describe your business..."
                rows="4"
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label>Timezone</label>
              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select timezone</option>
                <option value="UTC-8">Pacific Time (UTC-8)</option>
                <option value="UTC-5">Eastern Time (UTC-5)</option>
                <option value="UTC+0">GMT (UTC+0)</option>
                <option value="UTC+1">Central European Time (UTC+1)</option>
                <option value="UTC+5:30">India Standard Time (UTC+5:30)</option>
              </select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h2>Set your business hours</h2>
            <div className="form-group">
              <label>Operating Hours</label>
              <div className="time-inputs">
                <input
                  type="time"
                  value={formData.businessHours.start}
                  onChange={(e) => handleBusinessHoursChange('start', e.target.value)}
                  className="form-input"
                />
                <span>to</span>
                <input
                  type="time"
                  value={formData.businessHours.end}
                  onChange={(e) => handleBusinessHoursChange('end', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Operating Days</label>
              <div className="days-grid">
                {daysOfWeek.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`day-button ${formData.businessHours.days.includes(day) ? 'active' : ''}`}
                  >
                    {day.substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h2>Connect your WhatsApp Business Account</h2>
            <div className="form-group">
              <label>WhatsApp Business Account ID (WABA ID) *</label>
              <input
                type="text"
                name="wabaId"
                value={formData.wabaId}
                onChange={handleChange}
                placeholder="123456789012345"
                className="form-input"
                required
              />
              <p className="help-text">Find your WABA ID in Meta Business Manager → WhatsApp → Account Settings</p>
            </div>

            <div className="form-group">
              <label>Phone Number ID (Optional)</label>
              <input
                type="text"
                name="phoneNumberId"
                value={formData.phoneNumberId}
                onChange={handleChange}
                placeholder="102234567890123"
                className="form-input"
              />
              <p className="help-text">Find your Phone Number ID in WhatsApp Account → Phone Numbers → Select a number</p>
            </div>

            <div className="form-group">
              <label>Meta Access Token (Optional)</label>
              <input
                type="password"
                name="metaAccessToken"
                value={formData.metaAccessToken}
                onChange={handleChange}
                placeholder="Your Meta API access token"
                className="form-input"
              />
              <p className="help-text">
                Your access token for Meta Graph API. Get it from Meta Business Manager → Settings → Developers → Your Apps → WhatsApp
              </p>
            </div>

            <div className="info-box">
              <p>💡 <strong>Tip:</strong> You can manage your WhatsApp Business Account details in Settings after onboarding. All data is securely encrypted.</p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h2>Review your information</h2>
            <div className="review-section">
              <div className="review-item">
                <span className="review-label">Business Type:</span>
                <span className="review-value">{formData.businessType || 'Not set'}</span>
              </div>
              <div className="review-item">
                <span className="review-label">Industry:</span>
                <span className="review-value">{formData.industry || 'Not set'}</span>
              </div>
              <div className="review-item">
                <span className="review-label">Business Hours:</span>
                <span className="review-value">
                  {formData.businessHours.start} - {formData.businessHours.end}
                </span>
              </div>
              <div className="review-item">
                <span className="review-label">Operating Days:</span>
                <span className="review-value">
                  {formData.businessHours.days.length > 0
                    ? formData.businessHours.days.join(', ')
                    : 'Not set'}
                </span>
              </div>
              <div className="review-item">
                <span className="review-label">WABA ID:</span>
                <span className="review-value">{formData.wabaId || 'Not set'}</span>
              </div>
              <div className="review-item">
                <span className="review-label">Phone Number ID:</span>
                <span className="review-value">{formData.phoneNumberId || 'Not set'}</span>
              </div>
              <div className="review-item">
                <span className="review-label">Meta Access Token:</span>
                <span className="review-value">
                  {formData.metaAccessToken ? '••••••••' + formData.metaAccessToken.slice(-4) : 'Not set'}
                </span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="progress-header">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          <div className="steps-indicator">
            {steps.map(step => (
              <div
                key={step.number}
                className={`step-indicator ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
              >
                <div className="step-icon">{step.icon}</div>
                <span className="step-title">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="step-wrapper">
          {renderStepContent()}
        </div>

        <div className="step-actions">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="btn-secondary"
              disabled={loading}
            >
              Previous
            </button>
          )}
          <div className="spacer" />
          {currentStep < totalSteps ? (
            <button
              type="button"
              onClick={handleNext}
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Next'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleComplete}
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Completing...' : 'Complete Setup'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;

